"use client";

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import type { AdminUser, AuthUser } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { roleBadgeClass, roleLabel } from "@/components/booking/format";
import { Eye } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n/provider";

// Admin role-preview: pick a user to browse the app with their role and
// permissions. The real admin session stays alive in the background.
export function PreviewUsersModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const setUser = useAppStore((s) => s.setUser);
  const setImpersonating = useAppStore((s) => s.setImpersonating);
  const goBrowse = useAppStore((s) => s.goBrowse);
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiGet<{ users: AdminUser[] }>("/api/admin/users"),
    enabled: open,
  });

  const start = async (u: AdminUser) => {
    setBusyId(u.id);
    try {
      const res = await apiPost<{ user: AuthUser }>("/api/auth/impersonate", {
        userId: u.id,
      });
      qc.clear();
      setUser(res.user);
      setImpersonating(true);
      goBrowse();
      onClose();
      toast.success(
        t("preview.started", {
          name: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.username,
        })
      );
    } catch (err: any) {
      toast.error(err.message || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  // Admins are excluded: the admin console is already the admin's native view.
  const users = (data?.users ?? []).filter((u) => u.role !== "ADMIN");

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("preview.title")}</DialogTitle>
          <DialogDescription>{t("preview.hint")}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          {isLoading && <Skeleton className="h-16 w-full" />}
          {!isLoading && users.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          )}
          {users.map((u) => {
            const name =
              [u.firstName, u.lastName].filter(Boolean).join(" ") || u.username;
            return (
              <div
                key={u.id}
                className="flex items-center justify-between gap-3 rounded-lg border p-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{name}</div>
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-xs text-muted-foreground">
                      @{u.username}
                    </span>
                    <Badge
                      variant="outline"
                      className={`h-5 ${roleBadgeClass(u.role)}`}
                    >
                      {roleLabel(u.role, locale)}
                    </Badge>
                    {!u.validated && (
                      <Badge
                        variant="outline"
                        className="h-5 border-amber-300 bg-amber-50 text-amber-700"
                      >
                        {t("mod.notValidated")}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  disabled={busyId === u.id}
                  onClick={() => start(u)}
                >
                  <Eye className="h-3.5 w-3.5" /> {t("preview.preview")}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
